import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Target, CheckCircle } from 'lucide-react';
import { theme } from '../../theme';
import { API_BASE_URL, isGuestMode } from '../../config/api';

const MOCK_SEGMENTS = [
  { id: 1, name: '7-Day Streak', description: 'Go 7 consecutive days without non-essential spending', target_value: 7, segment_type: 'streak' },
  { id: 2, name: 'First Payment', description: 'Make your first extra debt payment beyond the minimum', target_value: 1, segment_type: 'streak' },
  { id: 3, name: '30-Day Warrior', description: 'Maintain a 30-day no-spend streak', target_value: 30, segment_type: 'streak' },
  { id: 4, name: 'Half Crushed', description: 'Pay off 50% of your total debt', target_value: 50, segment_type: 'percentage' },
  { id: 5, name: 'Debt Free', description: 'Pay off 100% of your tracked debt', target_value: 100, segment_type: 'percentage' },
];

const MOCK_PROGRESS = [
  { id: 1, completed: true, rank: 3 },
  { id: 2, completed: true, rank: 12 },
  { id: 3, completed: false },
  { id: 4, completed: false },
  { id: 5, completed: false },
];

export function ArenaSegments() {
  const [segments, setSegments] = useState<any[]>([]);
  const [progress, setProgress] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isGuestMode()) {
      setSegments(MOCK_SEGMENTS);
      setProgress(MOCK_PROGRESS);
      setLoading(false);
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const [segRes, progRes] = await Promise.all([
        fetch(`${API_BASE_URL}/arena/segments`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_BASE_URL}/arena/segments/progress`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (segRes.ok && progRes.ok) {
        const segData = await segRes.json();
        const progData = await progRes.json();
        setSegments(segData.segments);
        setProgress(progData.segment_progress);
      }
    } catch (err) {
      console.error('Failed to fetch segments:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div>Loading segments...</div>;

  const completedCount = progress.filter(p => p.completed).length;

  return (
    <div>
      <h2>Segments</h2>
      <p style={{ color: theme.semantic.text.secondary }}>Standardized challenges for all warriors</p>

      {/* Progress Overview */}
      <div style={{
        background: theme.semantic.bg.elevated,
        border: `1px solid ${theme.semantic.border.strong}`,
        borderRadius: theme.radii.md,
        padding: theme.spacing[5],
        marginBottom: theme.spacing[6],
      }}>
        <div style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, color: theme.semantic.text.primary }}>
          {completedCount} / {segments.length} Completed
        </div>
      </div>

      {/* Segments List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
        {segments.map((segment) => {
          const segmentProgress = progress.find(p => p.id === segment.id);
          const isCompleted = segmentProgress?.completed || false;

          return (
            <motion.div
              key={segment.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              style={{
                background: theme.semantic.bg.surface,
                border: `2px solid ${isCompleted ? theme.colors.success : theme.semantic.border.default}`,
                borderRadius: theme.radii.md,
                padding: theme.spacing[5],
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[4] }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  background: isCompleted ? `${theme.colors.success}22` : theme.brand.charcoal,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {isCompleted ? <CheckCircle size={28} color={theme.colors.success} /> : <Target size={28} />}
                </div>

                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0 }}>{segment.name}</h3>
                  <p style={{ color: theme.semantic.text.secondary }}>{segment.description}</p>
                  <div style={{ color: theme.brand.copper }}>
                    Target: {segment.target_value} {segment.segment_type === 'streak' ? 'days' : '%'}
                  </div>
                  {isCompleted && segmentProgress?.rank && (
                    <span style={{
                      background: theme.brand.copper,
                      color: theme.semantic.text.inverse,
                      padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
                      borderRadius: theme.radii.sm,
                    }}>
                      Rank #{segmentProgress.rank}
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}